import React, { useState } from 'react';
import styles from './MfConnectionForm.module.css';
import useTheme from 'config/useTheme';
import { ClosedEyeIcon, OpenEyeIcon } from 'assets/icons';

/**
 * Forma za izvor sa konekcijom, GENERISANA iz `configSchema` plugin-a.
 *
 * Ne zna ništa o MySQL-u ni o Mongo-u. Sve što joj treba — koja polja, kog
 * tipa, šta je obavezno, šta je tajna, koji je natpis — stiže sa servera. Zato
 * dodavanje novog izvora u `manifest.js` ovde ne traži nijednu izmenu, dok su
 * zatečeni `MongoImport.jsx` i `RedisImport.jsx` bila dva zasebna dijaloga sa
 * ručno prepisanim poljima.
 *
 * @param {object} props
 * @param {Array}  props.schema   configSchema plugin-a iz kataloga
 * @param {object} props.values   trenutne vrednosti
 * @param {(name: string, value: any) => void} props.onChange
 * @param {Array<string>} [props.hide]   polja koja `inspect` sklanja
 * @param {(field: object) => void} [props.onBrowse] Klik na dugme uz polje sa
 *   `browse`. Forma ne dohvata spisak sama — ona samo javlja da je zatraženo.
 * @param {string} [props.browsing] Ime polja za koje se spisak upravo čita
 */
const MfConnectionForm = ({
    schema = [],
    values = {},
    onChange,
    hide = [],
    onBrowse,
    browsing,
}) => {
    const { dark } = useTheme();

    // Koja su tajna polja trenutno otkrivena. Po imenu polja, jer ih na formi
    // ume biti više (lozinka i pun konekcioni string), a otkrivanje jednog ne
    // treba da otkrije i drugo.
    const [revealed, setRevealed] = useState({});
    const toggle = (name) => setRevealed((prev) => ({ ...prev, [name]: !prev[name] }));

    // Polja koja na KONKRETNOM serveru nemaju smisla — javlja ih `inspect`.
    // Redis ispod 6.0 nema korisnike (ACL), pa bi uneto ime samo odvelo na
    // pogrešan trag: server ga ne razume, a poruka govori o lozinci.
    const visible = schema.filter((f) => !hide.includes(f.name));

    const valueOf = (f) =>
        values[f.name] !== undefined && values[f.name] !== ''
            ? values[f.name]
            : (f.default !== undefined ? f.default : '');

    return (
        <div className={`${styles.connection_form} ${dark ? styles.dark : ''}`}>
            {visible.map((f) => {
                if (f.type === 'boolean') {
                    return (
                        <label key={f.name} className={styles.checkbox}>
                            <input
                                type="checkbox"
                                checked={Boolean(valueOf(f))}
                                onChange={(e) => onChange(f.name, e.target.checked)}
                            />
                            <span>{f.label || f.name}</span>
                        </label>
                    );
                }

                const isRevealed = Boolean(revealed[f.name]);

                const input = (
                    <input
                        // `secret` na polju znači lozinka ili konekcioni string —
                        // ne stoji u čistom tekstu na ekranu dok se ne zatraži.
                        type={
                            f.secret
                                ? (isRevealed ? 'text' : 'password')
                                : f.type === 'number' ? 'number' : 'text'
                        }
                        value={valueOf(f)}
                        autoComplete={f.secret ? 'new-password' : 'off'}
                        onChange={(e) =>
                            onChange(
                                f.name,
                                f.type === 'number'
                                    ? (e.target.value === '' ? '' : Number(e.target.value))
                                    : e.target.value,
                            )
                        }
                    />
                );

                return (
                    <label key={f.name} className={styles.row}>
                        <span className={styles.label}>
                            {f.label || f.name}
                            {f.required && <em className={styles.required}>*</em>}
                        </span>
                        {/* Polje koje se može popuniti nabrajanjem iz izvora.
                            Ostaje i dalje polje za kucanje: server koji ne daje
                            pravo na spisak (Mongo bez `listCollections`) ne sme
                            da onemogući uvoz otkucanog imena. */}
                        {f.browse && onBrowse ? (
                            <span className={styles.browsable}>
                                {input}
                                <button
                                    type="button"
                                    className={styles.browse}
                                    onClick={() => onBrowse(f)}
                                    disabled={browsing === f.name}
                                    title="Connect and list what the source contains"
                                >
                                    {browsing === f.name ? '…' : 'Browse'}
                                </button>
                            </span>
                        ) : f.secret ? (
                            // Isto oko kao u dijalogu za prijavu: lozinka koja se
                            // ne vidi se ne može ni proveriti, a ovde je najčešća
                            // greška u uvozu upravo pogrešno otkucana lozinka.
                            <span className={styles.secret}>
                                {input}
                                <button
                                    type="button"
                                    className={styles.reveal}
                                    onClick={() => toggle(f.name)}
                                    aria-label={isRevealed ? 'Hide' : 'Show'}
                                    title={isRevealed ? 'Hide' : 'Show'}
                                >
                                    {isRevealed ? <OpenEyeIcon /> : <ClosedEyeIcon />}
                                </button>
                            </span>
                        ) : (
                            input
                        )}
                    </label>
                );
            })}
        </div>
    );
};

/** Nedostajuća obavezna polja — pozivalac time gasi dugme „dalje". */
export const missingRequired = (schema = [], values = {}) =>
    schema
        .filter((f) => f.required)
        .filter((f) => {
            const v = values[f.name] !== undefined ? values[f.name] : f.default;
            return v === undefined || v === '' || v === null;
        })
        .map((f) => f.label || f.name);

export default MfConnectionForm;
